import { useMemo } from 'react';
import { Box, Button, MenuItem, Select, Typography } from '@mui/material';
import { v4 } from '@rotra-air-ocean/rotranext-shared-code';
import {
  Background,
  type Connection,
  Controls,
  type Edge,
  Handle,
  type Node,
  type NodeProps,
  Position,
  ReactFlow,
} from '@xyflow/react';
import { Play, Plus, Variable } from 'lucide-react';

import { KIND_META, KindIcon } from '../consts';
import { stepTransitions, stepWarnings } from '../helpers/context';
import { layoutGraph } from '../helpers/layout';
import { specToGraph } from '../helpers/specToGraph';

import { Stack } from './Stack';

import '@xyflow/react/dist/style.css';

interface EditorNodeData extends Record<string, unknown> {
  label: string;
  kind: v4.StepKind;
  isStart: boolean;
  selected: boolean;
  active: boolean;
  visited: boolean;
  connectable: boolean;
  canTarget: boolean;
  canSource: boolean;
  warnings: string[];
  onSelect?: (id: string) => void;
}

function EditorStepNode({ id, data }: NodeProps<Node<EditorNodeData>>) {
  const km = KIND_META[data.kind];
  return (
    <div
      className={[
        'wfc-node',
        `kind-${data.kind}`,
        data.selected ? 'selected' : '',
        data.active ? 'active' : '',
        data.visited ? 'visited' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ '--kind-color': km?.color } as React.CSSProperties}
      onClick={() => data.onSelect?.(id)}
      title={data.warnings.length > 0 ? data.warnings.join('\n') : undefined}
    >
      {data.canTarget && (
        <Handle
          type="target"
          position={Position.Top}
          isConnectable={data.connectable}
        />
      )}
      <span className="wfc-node-kind" style={{ color: km?.color }}>
        <KindIcon kind={data.kind} size={13} />
        {km?.label ?? data.kind}
      </span>
      <span className="wfc-node-label">{data.label || id}</span>
      <span className="wfc-node-id">{id}</span>
      <div className="wfc-node-badges">
        {data.isStart && <span className="wfc-start">start</span>}
        {data.warnings.length > 0 && (
          <span className="wfc-warn" title={data.warnings.join('\n')}>
            !
          </span>
        )}
      </div>
      {data.canSource && (
        <Handle
          type="source"
          position={Position.Bottom}
          isConnectable={data.connectable}
        />
      )}
    </div>
  );
}

const nodeTypes = { editorStep: EditorStepNode };

export type WorkflowCanvasMode = 'edit' | 'inspect' | 'view';

interface Props {
  trigger: 'email' | 'workflow';
  spec: v4.WorkflowSpec;
  title: string;
  workflowId: string;
  /**
   * `edit` allows authoring changes, `inspect` shows the authoring canvas in a
   * read-only state, and `view` shows runtime progress.
   */
  mode: WorkflowCanvasMode;
  selectedStepId?: string | null;
  activeStepId?: string | null;
  visitedStepIds?: string[];
  onSelectStep?: (id: string) => void;
  onConnect?: (source: string, target: string) => void;
  onClearTransition?: (source: string, target: string, label: string) => void;
  onAddStep?: (source?: string) => void;
  onSetInitial?: (stepId: string) => void;
  onEditInputs?: () => void;
  onViewInputs?: () => void;
}

/**
 * Workflow diagram with three modes:
 * - `edit` is the primary authoring surface: add steps, connect transitions,
 *   and remove selected edges.
 * - `inspect` uses the same selection and validation affordances as `edit`,
 *   but deliberately exposes no mutation controls.
 * - `view` shows runtime progress through the workflow.
 *
 * Layout is derived from the spec (dagre), so positions are never persisted.
 */
export function WorkflowCanvas({
  trigger,
  spec,
  title,
  workflowId,
  mode,
  selectedStepId,
  activeStepId,
  visitedStepIds = [],
  onSelectStep,
  onConnect,
  onClearTransition,
  onAddStep,
  onSetInitial,
  onEditInputs,
  onViewInputs,
}: Props) {
  const stepIds = Object.keys(spec.steps);
  const isEdit = mode === 'edit';
  const isInspecting = mode === 'edit' || mode === 'inspect';
  const isRuntimeView = mode === 'view';

  // Layout depends only on structure (ids/kinds/transitions/initial), so editing
  // a label or selecting a node doesn't reshuffle or remount the canvas.
  const structureKey = useMemo(
    () =>
      JSON.stringify({
        initial: spec.initial,
        steps: Object.fromEntries(
          Object.entries(spec.steps).map(([id, s]) => [
            id,
            { kind: s.kind, t: stepTransitions(s) },
          ]),
        ),
      }),
    [spec],
  );

  const laid = useMemo(() => {
    const g = specToGraph(workflowId, title, spec);
    const wf = g.workflows[g.root];
    return layoutGraph(wf.nodes, wf.edges);
  }, [structureKey, workflowId]);

  const visited = useMemo(() => new Set(visitedStepIds), [visitedStepIds]);

  const nodes: Node<EditorNodeData>[] = useMemo(
    () =>
      laid.nodes.map((n) => ({
        id: n.id,
        type: 'editorStep',
        position: n.position,
        deletable: false,
        sourcePosition: Position.Bottom,
        targetPosition: Position.Top,
        data: {
          label: spec.steps[n.id]?.label || n.id,
          kind: n.data.kind as v4.StepKind,
          isStart: n.id === spec.initial,
          selected: isInspecting && n.id === selectedStepId,
          active: isRuntimeView && n.id === activeStepId,
          visited: isRuntimeView && visited.has(n.id),
          connectable: isEdit,
          canSource: n.data.kind !== 'final',
          canTarget: true,
          warnings:
            isInspecting && spec.steps[n.id]
              ? stepWarnings(trigger, spec, n.id, spec.steps[n.id])
              : [],
          onSelect: onSelectStep,
        },
      })),
    [
      laid,
      spec,
      isEdit,
      isInspecting,
      isRuntimeView,
      trigger,
      selectedStepId,
      activeStepId,
      visited,
      onSelectStep,
    ],
  );

  const edges: Edge[] = useMemo(
    () =>
      laid.edges.map((e) => {
        const traversed =
          isRuntimeView &&
          visited.has(e.source) &&
          (visited.has(e.target) || e.target === activeStepId);
        return {
          ...e,
          deletable: isEdit,
          animated: isEdit ? e.animated : traversed,
          className: [e.className, traversed ? 'wfc-edge-traversed' : '']
            .filter(Boolean)
            .join(' '),
        };
      }),
    [laid, isEdit, isRuntimeView, visited, activeStepId],
  );

  const handleConnect = (c: Connection) => {
    if (!isEdit || !c.source || !c.target) {
      return;
    }
    onConnect?.(c.source, c.target);
  };

  const handleEdgesDelete = (eds: Edge[]) => {
    if (!isEdit) {
      return;
    }

    for (const edge of eds) {
      const label =
        typeof edge.label === 'string' || typeof edge.label === 'number'
          ? String(edge.label)
          : '';
      onClearTransition?.(edge.source, edge.target, label);
    }
  };

  return (
    <Box
      className="wfc"
      sx={{
        position: 'relative',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {(isEdit || onViewInputs) && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="flex-end"
          sx={{
            p: 1,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            zIndex: 1,
            flexWrap: 'wrap',
            rowGap: 0.5,
          }}
        >
          {isEdit && onEditInputs && (
            <Button
              startIcon={<Variable size={14} />}
              onClick={onEditInputs}
              title="Declare the inputs this workflow needs when used as a sub-workflow"
            >
              Inputs
            </Button>
          )}

          {!isEdit && onViewInputs && (
            <Button
              startIcon={<Variable size={14} />}
              onClick={onViewInputs}
              title="View the inputs and outputs declared by this workflow"
            >
              View inputs
            </Button>
          )}

          {isEdit && onSetInitial && stepIds.length > 0 && (
            <Select<string>
              size="small"
              value={spec.initial}
              displayEmpty
              aria-label="Workflow start step"
              onChange={(event) => {
                onSetInitial(event.target.value);
              }}
              renderValue={(selected) => {
                const startStep = spec.steps[selected];
                const prefix = 'Start';

                return (
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Play size={14} />
                    <Typography variant="body2" noWrap>
                      {startStep
                        ? `${prefix}: ${startStep.label || selected}`
                        : 'Choose start'}
                    </Typography>
                  </Stack>
                );
              }}
              MenuProps={{
                slotProps: { paper: { sx: { minWidth: 230, mt: 0.5 } } },
              }}
              sx={{
                minWidth: { xs: 145, sm: 205 },
                maxWidth: 250,
                bgcolor: 'background.default',
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  py: '5px !important',
                },
              }}
            >
              <MenuItem value="" disabled>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Choose a start step
                </Typography>
              </MenuItem>
              {stepIds.map((id) => {
                const step = spec.steps[id];

                return (
                  <MenuItem key={id} value={id} sx={{ py: 0.75 }}>
                    <Box sx={{ color: KIND_META[step.kind]?.color, mr: 1 }}>
                      <KindIcon kind={step.kind} size={15} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {step.label || id}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        {id}
                      </Typography>
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          )}

          {isEdit && (
            <Button
              variant="contained"
              startIcon={<Plus size={14} />}
              onClick={() => onAddStep?.()}
            >
              Add step
            </Button>
          )}
        </Stack>
      )}

      {stepIds.length === 0 ? (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={2}
          sx={{ flexGrow: 1, color: 'text.secondary' }}
        >
          <Typography>This workflow is empty.</Typography>
          {isEdit && (
            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              onClick={() => onAddStep?.()}
            >
              Add the first step
            </Button>
          )}
        </Stack>
      ) : (
        <Box sx={{ flexGrow: 1, position: 'relative' }}>
          <ReactFlow
            key={`${structureKey}:${mode}`}
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            fitView
            nodesDraggable={false}
            nodesConnectable={isEdit}
            elementsSelectable
            selectNodesOnDrag={false}
            deleteKeyCode={isEdit ? ['Delete', 'Backspace'] : null}
            onConnect={isEdit ? handleConnect : undefined}
            onEdgesDelete={isEdit ? handleEdgesDelete : undefined}
            proOptions={{ hideAttribution: true }}
            onConnectEnd={(e, connectionState) => {
              // The user released the connection on empty canvas.
              if (!connectionState.isValid && connectionState.fromNode) {
                onAddStep?.(connectionState.fromNode.id);
              }
            }}
          >
            <Background />
            <Controls showInteractive={false} />
          </ReactFlow>
        </Box>
      )}
    </Box>
  );
}
